import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { CopyGuard } from "@/components/CopyGuard";

import Home from "@/pages/Home";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Research from "@/pages/Research";
import ResearchDetail from "@/pages/ResearchDetail";
import Tools from "@/pages/Tools";
import Scanners from "@/pages/Scanners";
import ParticipantOI from "@/pages/ParticipantOI";
import Events from "@/pages/Events";
import Books from "@/pages/Books";
import Learn from "@/pages/Learn";
import StrategyDetail from "@/pages/StrategyDetail";
import KnowledgeCenter from "@/pages/KnowledgeCenter";
import ArticleDetail from "@/pages/ArticleDetail";
import Glossary from "@/pages/Glossary";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Disclaimer from "@/pages/Disclaimer";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyEmailChange from "@/pages/VerifyEmailChange";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Classroom from "@/pages/Classroom";
import AccountDeletion from "@/pages/AccountDeletion";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (!user) {
    window.location.href = "/login";
    return null;
  }
  if (admin && user.role !== "admin") {
    window.location.href = "/dashboard";
    return null;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <CopyGuard />
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:slug" element={<CourseDetail />} />
            <Route path="/courses/:slug/learn" element={<Protected><Classroom /></Protected>} />
            <Route path="/research" element={<Research />} />
            <Route path="/research/:slug" element={<ResearchDetail />} />
            <Route path="/tools" element={<Navigate to="/calculators" replace />} />
            <Route path="/calculators" element={<Tools />} />
            <Route path="/scanners" element={<Scanners />} />
            <Route path="/participant-oi" element={<ParticipantOI />} />
            <Route path="/events" element={<Events />} />
            <Route path="/books" element={<Books />} />
            <Route path="/strategy" element={<Learn />} />
            <Route path="/strategy/:slug" element={<StrategyDetail />} />
            <Route path="/learn" element={<Navigate to="/strategy" replace />} />
            <Route path="/learn/:slug" element={<StrategyDetail />} />
            <Route path="/knowledge" element={<KnowledgeCenter />} />
            <Route path="/knowledge/:slug" element={<ArticleDetail />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-email-change" element={<VerifyEmailChange />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
